export interface Volume {
  number: string
  title: string
  subtitle: string
  bullets: string[]
  result: string
  featured?: boolean
  badge?: string
}

export const volumes: Volume[] = [
  {
    number: 'Volume 1',
    title: 'Visual Art Foundations & Creative Language',
    subtitle: 'The foundational base that holds everything together.',
    bullets: [
      'Line, shape and form',
      'Color and value',
      'Texture and space',
      'Pattern and rhythm',
      'Contrast',
      'Material exploration',
      'Creative decision-making',
      'Visual analysis',
      'Classroom projects and assessment tools',
    ],
    result:
      'Your students understand the essential visual language of art and begin making stronger creative decisions from their first classes.',
  },
  {
    number: 'Volume 2',
    title: 'Drawing Foundations & Visual Observation',
    subtitle:
      'Develop observation, proportion, structure and visual confidence.',
    bullets: [
      'Line control and contour',
      'Shape and form construction',
      'Observation systems',
      'Comparative measurement',
      'Proportion',
      'Negative space',
      'Value and light',
      'Texture and still-life studies',
      'Correction strategies',
    ],
    result:
      'Students stop guessing and start using visible relationships, observation and structure.',
  },
  {
    number: 'Volume 3',
    title: 'Painting, Color & Water-Based Media',
    subtitle: 'Teach students how paint behaves and how to control it.',
    bullets: [
      'Watercolor, acrylic and gouache',
      'Brush control and washes',
      'Wet-on-wet and wet-on-dry',
      'Layering, transparency and opacity',
      'Edge control and texture',
      'Color application and corrections',
      'Landscapes and botanical painting',
      'Abstract painting',
    ],
    result:
      'Students gain practical control over color, water, brushes, layers, edges and painted surfaces.',
  },
  {
    number: 'Volume 4',
    title: 'Composition, Design & Visual Communication',
    subtitle:
      'Teach students how to organize visual information and communicate through art.',
    bullets: [
      'Balance and focal point',
      'Hierarchy and framing',
      'Cropping',
      'Movement and rhythm',
      'Contrast and emphasis',
      'Negative space',
      'Poster design and symbolism',
      'Visual storytelling with image and text',
    ],
    result:
      'Students stop simply filling the page and begin controlling what viewers notice and how an artwork communicates.',
  },
  {
    number: 'Volume 5',
    title: 'Figure, Portrait, Nature & Subject Studies',
    subtitle:
      'Teach students how to observe, simplify and interpret real-world subjects.',
    bullets: [
      'Gesture and figure',
      'Human proportion',
      'Portrait and facial structure',
      'Hands, feet and foreshortening',
      'Plants, flowers and trees',
      'Animals and natural forms',
      'Architecture and environmental studies',
      'Stylization',
    ],
    result:
      'Students build the confidence to study and interpret people, nature and environmental subjects.',
  },
  {
    number: 'Volume 6',
    title:
      'Mixed Media, Collage, Printmaking, Sculpture & Creative Art Practice',
    subtitle: 'Move beyond pencil and paint into broader art-making.',
    bullets: [
      'Mixed media and collage',
      'Painted paper, texture and stamping',
      'Printmaking, monoprint and stencils',
      'Paper sculpture and cardboard construction',
      'Wire, assemblage and found objects',
      'Recycled materials',
      'Abstract art and installation',
      'Collaborative and experimental art',
    ],
    result:
      'Students learn to build, print, layer, assemble and transform materials across 2D, 3D and spatial art.',
  },
  {
    number: 'Volume 7',
    title: 'Visual Art Classroom Atlas',
    subtitle: 'The visual heart of the complete system.',
    bullets: [
      '100+ visual demonstrations',
      'Elements of Art and Principles of Design references',
      'Watercolor, acrylic and gouache visual atlases',
      'Brush-mark library and collage examples',
      'Mixed-media, printmaking and stencil references',
      'Texture libraries and paper-art demonstrations',
      'Cardboard construction and sculpture references',
      '30+ ready-to-teach visual art projects',
      'Visual style library and palette systems',
      '50 visual prompts and 30 art challenges',
      'Project generator, critique and revision boards',
      'Classroom displays and exhibition ideas',
    ],
    result:
      "Open, show, explain, and create. Your students can see what you're teaching before they begin.",
    featured: true,
    badge: 'MOST VISUAL',
  },
]

export interface LessonGroup {
  title: string
  detail: string
}

export const lessonGroups: LessonGroup[] = [
  {
    title: 'Visual Art Foundations',
    detail: 'Line, shape, form, color, value, texture and space.',
  },
  {
    title: 'Drawing & Observation',
    detail:
      'Contour, proportion, measurement, observation and negative space.',
  },
  {
    title: 'Painting & Color',
    detail: 'Watercolor, acrylic, gouache, brushwork and layering.',
  },
  {
    title: 'Composition & Design',
    detail: 'Balance, hierarchy, movement, contrast and visual communication.',
  },
  {
    title: 'Figure & Portrait',
    detail:
      'Gesture, human proportion, portrait structure and facial studies.',
  },
  {
    title: 'Nature & Subject Studies',
    detail: 'Plants, flowers, trees, animals, architecture and environment.',
  },
  {
    title: 'Collage & Mixed Media',
    detail: 'Painted paper, layering, texture and material combinations.',
  },
  {
    title: 'Printmaking',
    detail: 'Stamping, stencil, monoprint, relief, repetition and overprinting.',
  },
  {
    title: 'Sculpture & 3D Art',
    detail: 'Paper, cardboard, volume, balance, negative space and assemblage.',
  },
  {
    title: 'Creative Art Practice',
    detail:
      'Abstract art, installation, experimental art and collaborative projects.',
  },
]
